import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { MapAnalysisQuote } from '../response-models/map-analysis-quote.model';

/**
 * Map Analysis Product / Quote Service
 *
 * Per-acre pricing model (locked 2026-05-19 — see billing-payments § 9).
 * The single Stripe Product ("Map Analysis Per Acre") is referenced via the
 * `STRIPE_PRODUCTS.map_analysis_per_acre` env key on the Hub. The web never
 * needs to know the Stripe IDs — it just asks the Hub for a quote keyed to
 * the field's acreage.
 *
 * The deprecated credit-pack model (creditCount + priceCents) is gone — see
 * billing-payments § 9.2 for the rationale. `getQuote(acres)` is the only
 * supported public API; the previous `getDefault()` helper was removed
 * along with the credit-pack model. `AnalysisFormComponent` is the
 * primary consumer.
 */
@Injectable({ providedIn: 'root' })
export class MapAnalysisProductService extends ApiService {
  constructor() {
    super('payments/map-analysis');
  }

  /**
   * Returns a fresh per-acre quote from the Hub. The Hub reads the
   * `unit_amount` straight from the live Stripe Price, so this is always
   * up-to-date — bump the price in Stripe Dashboard and the next quote
   * reflects it without a deploy.
   */
  async getQuote(acres: number, productKey: string = 'map_analysis_per_acre'): Promise<MapAnalysisQuote> {
    const quote = await firstValueFrom(
      this.request('GET', { path: 'quote', query: { acres, productKey } }, data => (data ?? null) as MapAnalysisQuote | null)
    );
    if (!quote) {
      throw new Error('Failed to load map-analysis quote');
    }
    return quote;
  }

  /**
   * Formats a cents amount in the given ISO 4217 currency using the en-US
   * locale (e.g., 835, 'usd' → "$8.35"). Defaults to USD because every
   * Stripe Price on the ADN platform is USD today; pass the quote's own
   * `currency` field if that ever changes. The locale is hard-coded to
   * en-US — number formatting (commas vs. periods, currency placement)
   * always matches the ADN web app's English-only UI.
   */
  formatCents(cents: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  }
}
